import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  Reply as ReplyIcon,
  Done as DoneIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  SmartToy as AIIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface InboxItem {
  id: string;
  platform: string;
  type: string; // 'comment', 'dm', 'mention'
  from_user: string;
  from_user_avatar?: string;
  message: string;
  post_context?: string;
  is_read: boolean;
  suggested_reply?: string;
  created_at: string;
}

const platformIcons: Record<string, React.ReactNode> = {
  linkedin: <LinkedInIcon fontSize="small" />,
  twitter: <TwitterIcon fontSize="small" />,
  facebook: <FacebookIcon fontSize="small" />,
  instagram: <InstagramIcon fontSize="small" />,
};

export default function Inbox() {
  const [items, setItems] = useState<InboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [showReplyDialog, setShowReplyDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InboxItem | null>(null);
  const [replyText, setReplyText] = useState('');

  useEffect(() => {
    loadInboxItems();
  }, []);

  const loadInboxItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/inbox`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setItems(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load inbox');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenReply = (item: InboxItem) => {
    setSelectedItem(item);
    setReplyText(item.suggested_reply || '');
    setShowReplyDialog(true);
  };

  const handleSendReply = async () => {
    if (!selectedItem) return;

    try {
      await axios.post(
        `${API_URL}/inbox/${selectedItem.id}/reply`,
        { message: replyText },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        }
      );

      setShowReplyDialog(false);
      setSelectedItem(null);
      setReplyText('');
      loadInboxItems();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send reply');
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await axios.patch(
        `${API_URL}/inbox/${id}/read`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        }
      );
      loadInboxItems();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to mark as read');
    }
  };

  const handleGenerateSuggestedReply = async () => {
    if (!selectedItem) return;

    try {
      // Step 1: Create reply suggestion workflow via orchestrator
      const response = await axios.post(
        `${API_URL}/inbox/${selectedItem.id}/suggest-reply`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        }
      );
      
      // Check if response contains workflow_id (orchestrator-powered endpoint)
      if (response.data.workflow_id) {
        const workflowId = response.data.workflow_id;
        setSuccess(`Reply workflow created! Workflow ID: ${workflowId}. Executing Engagement Agent...`);
        
        // Step 2: Automatically execute the workflow (orchestrator delegates to Engagement Agent)
        const executeResponse = await axios.post(
          `${API_URL}/workflows/${workflowId}/execute`,
          {},
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
          }
        );
        
        setSuccess(`Reply generation executing! Engagement Agent is crafting personalized responses. View progress in Workflows page.`);
        
        // Check for suggested_reply in the response as workflow may complete immediately
        if (response.data.suggested_reply) {
          setReplyText(response.data.suggested_reply);
        }
      } else {
        // Legacy direct response
        setReplyText(response.data.suggested_reply);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to generate suggested reply');
    }
  };

  const filteredItems = items.filter((item) => {
    if (tabValue === 0) return !item.is_read;
    if (tabValue === 1) return item.type === 'comment';
    if (tabValue === 2) return item.type === 'dm';
    return item.type === 'mention';
  });

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: { xs: 2, sm: 3 },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 },
        width: '100%'
      }}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Engagement Inbox
        </Typography>
        <Chip
          label={`${items.filter(i => !i.is_read).length} unread`}
          color="primary"
        />
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label="Unread" />
            <Tab label="Comments" />
            <Tab label="Direct Messages" />
            <Tab label="Mentions" />
          </Tabs>
        </CardContent>
      </Card>

      <Card>
        <List>
          {filteredItems.map((item, index) => (
            <React.Fragment key={item.id}>
              {index > 0 && <Divider />}
              <ListItem
                alignItems="flex-start"
                sx={{
                  bgcolor: item.is_read ? 'background.paper' : 'action.hover',
                }}
                secondaryAction={
                  <Box>
                    <IconButton
                      edge="end"
                      onClick={() => handleOpenReply(item)}
                      color="primary"
                    >
                      <ReplyIcon />
                    </IconButton>
                    {!item.is_read && (
                      <IconButton
                        edge="end"
                        onClick={() => handleMarkAsRead(item.id)}
                      >
                        <DoneIcon />
                      </IconButton>
                    )}
                  </Box>
                }
              >
                <ListItemAvatar>
                  <Avatar src={item.from_user_avatar}>
                    {item.from_user.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2">
                        {item.from_user}
                      </Typography>
                      {platformIcons[item.platform]}
                      <Chip label={item.type} size="small" variant="outlined" />
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        component="span"
                        display="block"
                        sx={{ mt: 1 }}
                      >
                        {item.message}
                      </Typography>
                      {item.post_context && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          component="span"
                          display="block"
                          sx={{ mt: 1, fontStyle: 'italic' }}
                        >
                          On post: "{item.post_context}"
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="span"
                        display="block"
                        sx={{ mt: 1 }}
                      >
                        {new Date(item.created_at).toLocaleString()}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            </React.Fragment>
          ))}
          {filteredItems.length === 0 && (
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2" color="text.secondary" align="center">
                    No items found
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>
      </Card>

      {/* Reply Dialog */}
      <Dialog open={showReplyDialog} onClose={() => setShowReplyDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Reply to {selectedItem?.from_user}
        </DialogTitle>
        <DialogContent>
          {selectedItem && (
            <>
              <Card sx={{ mb: 2, bgcolor: 'action.hover' }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    Original Message
                  </Typography>
                  <Typography variant="body2">
                    {selectedItem.message}
                  </Typography>
                </CardContent>
              </Card>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  startIcon={<AIIcon />}
                  onClick={handleGenerateSuggestedReply}
                  size="small"
                >
                  Generate AI Reply
                </Button>
              </Box>

              <TextField
                fullWidth
                label="Your Reply"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                multiline
                rows={4}
                helperText={selectedItem.suggested_reply && 'AI suggested reply loaded'}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReplyDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSendReply}
            disabled={!replyText}
          >
            Send Reply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
